import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Plus, FileText, X, Upload, Sparkles, Loader2, Search, Trash2, Eye, Layers,
} from 'lucide-react'
import { toast } from 'sonner'
import { logger } from '@/lib/utils/logger'
import {
    useReqSpecs,
    useCreateReqSpec,
    useDeleteReqSpec,
    useGenerateRequirements,
    useDRDs,
    useDeleteDRD,
} from '@/features/requirements/api/useRequirements'
import { useProjects } from '@/features/projects/api/useProjects'
import { useAttachments, useUploadAttachment } from '@/features/files/api/useFiles'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
    Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card'
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
    DialogTitle, DialogTrigger,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { formatDate } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Types for unified list
// ---------------------------------------------------------------------------
interface UnifiedItem {
    id: number
    itemType: 'spec' | 'drd'
    displayName: string
    displayId: string
    displayType: string
    projectName?: string
    requirementCount?: number
    createdAt: string
    title?: string
}

// fk_table used for codeset attachments — must match VALID_FK_TABLES DB constraint
const CODESET_FK_TABLE = 'requirement'

const PRESET_LAYERS = ['Backend', 'Frontend', 'Middleware', 'LLM / AI', 'Infrastructure']

// Generation cascade options
type GenerationOption = 'none' | 'testcases' | 'testcases_and_plan'

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const ReqSpecsPage = () => {
    const nav = useNavigate()

    // Filters
    const [projectFilter, setProjectFilter] = useState('all')
    const [typeFilter, setTypeFilter] = useState('all')
    const [search, setSearch] = useState('')

    // Create dialog
    const [createOpen, setCreateOpen] = useState(false)
    const [newName, setNewName] = useState('')
    const [newDocId, setNewDocId] = useState('')
    const [newTitle, setNewTitle] = useState('')
    const [newScope, setNewScope] = useState('')
    const [newType, setNewType] = useState('SRS')
    const [newProjectId, setNewProjectId] = useState('')

    // Delete confirmation
    const [deleteTarget, setDeleteTarget] = useState<UnifiedItem | null>(null)

    // Generation form
    const [genProjectId, setGenProjectId] = useState('')
    const [genLanguage, setGenLanguage] = useState('python')
    const [genFile, setGenFile] = useState<File | null>(null)
    const [genExistingFileId, setGenExistingFileId] = useState('')
    const [useExistingFile, setUseExistingFile] = useState(false)
    const [genOption, setGenOption] = useState<GenerationOption>('none')
    const [genLayer, setGenLayer] = useState('')
    const [genLayerCustom, setGenLayerCustom] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Derived cascade flags from the generation option
    const genTestCases = genOption === 'testcases' || genOption === 'testcases_and_plan'
    const genTestPlan  = genOption === 'testcases_and_plan'

    // Resolved layer value — custom input takes over when "custom" is selected
    const resolvedLayer = genLayer === 'custom'
        ? genLayerCustom.trim().toLowerCase().replace(/\s+/g, '_')
        : genLayer.toLowerCase().replace(/[\s/]+/g, '_')

    // Data fetching
    const { data: projects } = useProjects()
    const { data: reqSpecs, isLoading: specsLoading } = useReqSpecs(
        projectFilter !== 'all'
            ? { project_id: parseInt(projectFilter, 10) }
            : typeFilter !== 'all'
                ? { type: typeFilter }
                : {},
    )
    const { data: drds, isLoading: drdsLoading } = useDRDs(
        projectFilter !== 'all'
            ? { project_id: parseInt(projectFilter, 10) }
            : {},
    )

    const attachmentsParams = genProjectId
        ? { fk_table: CODESET_FK_TABLE, fk_id: parseInt(genProjectId, 10) }
        : { fk_table: CODESET_FK_TABLE }

    const { data: existingAttachments } = useAttachments(attachmentsParams)

    // Mutations
    const createMut = useCreateReqSpec()
    const deleteMut = useDeleteReqSpec()
    const deleteDRDMut = useDeleteDRD()
    const generateMut = useGenerateRequirements()
    const uploadAttachment = useUploadAttachment()

    // Deduplicate attachments
    const uniqueAttachments = useMemo(() => {
        if (!existingAttachments) return []
        return Array.from(
            new Map(existingAttachments.map((a) => [a.id, a])).values(),
        )
    }, [existingAttachments])

    // Build unified list
    const allItems: UnifiedItem[] = useMemo(() => {
        const specs = (reqSpecs || []).map((s) => ({
            id: s.id,
            itemType: 'spec' as const,
            displayName: s.name,
            displayId: s.doc_id,
            displayType: s.type,
            projectName: s.project_name,
            requirementCount: s.requirement_count ?? 0,
            createdAt: s.created_at,
            title: s.title,
        }))
        const drdItems = (drds || []).map((d) => ({
            id: d.id,
            itemType: 'drd' as const,
            displayName: d.title,
            displayId: `DRD-${d.id}`,
            displayType: 'DRD',
            projectName: d.project_name,
            requirementCount: d.requirement_count ?? 0,
            createdAt: d.created_at,
            title: d.title,
        }))
        return [...specs, ...drdItems]
    }, [reqSpecs, drds])

    // Filter
    const filtered = useMemo(() => {
        const q = search.toLowerCase()
        return allItems.filter((item) => {
            const matchSearch =
                !q ||
                item.displayName?.toLowerCase().includes(q) ||
                item.displayId.toLowerCase().includes(q) ||
                item.title?.toLowerCase().includes(q)
            const matchType =
                typeFilter === 'all' ||
                (item.itemType === 'spec' && item.displayType === typeFilter) ||
                (item.itemType === 'drd' && typeFilter === 'DRD')
            return matchSearch && matchType
        })
    }, [allItems, search, typeFilter])

    const isLoading = specsLoading || drdsLoading
    const hasFilters = projectFilter !== 'all' || typeFilter !== 'all' || search !== ''

    // -------------------------------------------------------------------------
    // Handlers
    // -------------------------------------------------------------------------
    const handleCreate = async () => {
        if (!newName.trim() || !newDocId.trim() || !newProjectId) {
            toast.error('Please fill in all required fields')
            return
        }
        try {
            await createMut.mutateAsync({
                name: newName.trim(),
                doc_id: newDocId.trim(),
                test_project_id: parseInt(newProjectId, 10),
                title: newTitle.trim() || undefined,
                scope: newScope.trim() || undefined,
                type: newType,
            })
            toast.success('Specification created successfully')
            setCreateOpen(false)
            resetCreateForm()
        } catch (err) {
            logger.error('Failed to create specification:', err)
            toast.error('Failed to create specification. Please try again.')
        }
    }

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return
        try {
            if (deleteTarget.itemType === 'drd') {
                await deleteDRDMut.mutateAsync(deleteTarget.id)
            } else {
                await deleteMut.mutateAsync(deleteTarget.id)
            }
            toast.success(
                `${deleteTarget.itemType === 'drd' ? 'DRD' : 'Specification'} deleted`,
            )
            setDeleteTarget(null)
        } catch (err) {
            logger.error('Failed to delete:', err)
            toast.error('Delete failed. Please try again.')
            setDeleteTarget(null)
        }
    }

    const handleGenerate = async () => {
        if (!genProjectId) {
            toast.error('Please select a project')
            return
        }
        if (!useExistingFile && !genFile) {
            toast.error('Please upload a codeset file or select an existing one')
            return
        }
        if (useExistingFile && !genExistingFileId) {
            toast.error('Please select an existing file')
            return
        }
        if (genLayer === 'custom' && !genLayerCustom.trim()) {
            toast.error('Please enter a custom layer name')
            return
        }

        try {
            setIsSubmitting(true)
            let attachmentId: number

            if (useExistingFile) {
                attachmentId = parseInt(genExistingFileId, 10)
            } else {
                const uploaded = await uploadAttachment.mutateAsync({
                    file: genFile!,
                    fk_id: parseInt(genProjectId, 10),
                    fk_table: CODESET_FK_TABLE,
                    title: 'Codeset for Requirements Generation',
                    description: `${genLanguage} codeset for automated requirements generation`,
                })
                attachmentId = uploaded.id
            }

            await generateMut.mutateAsync({
                project_id: parseInt(genProjectId, 10),
                attachment_id: attachmentId.toString(),
                language: genLanguage,
                generate_test_cases: genTestCases,
                generate_test_plan: genTestPlan,
                layer: resolvedLayer || undefined,
            })

            toast.success(
                genTestPlan
                    ? 'Requirements, test cases, and test plan generation submitted!'
                    : genTestCases
                        ? 'Requirements and test case generation submitted!'
                        : 'Requirements generation job submitted! Results will appear once processing completes.',
            )
            resetGenForm()
        } catch (err) {
            logger.error('Failed to generate requirements:', err)
            toast.error('Failed to submit generation job. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    const resetCreateForm = () => {
        setNewName('')
        setNewDocId('')
        setNewTitle('')
        setNewScope('')
        setNewType('SRS')
        setNewProjectId('')
    }

    const resetGenForm = () => {
        setGenProjectId('')
        setGenLanguage('python')
        setGenFile(null)
        setGenExistingFileId('')
        setUseExistingFile(false)
        setGenOption('none')
        setGenLayer('')
        setGenLayerCustom('')
    }

    const clearFilters = () => {
        setProjectFilter('all')
        setTypeFilter('all')
        setSearch('')
    }

    const navigateToItem = (item: UnifiedItem) => {
        if (item.itemType === 'drd') {
            nav(`/requirements-ops/requirements?drd_id=${item.id}`)
        } else {
            nav(`/requirements-ops/requirements?srs_id=${item.id}`)
        }
    }

    // -------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------
    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold">Requirement Specifications</h1>
                    <p className="text-muted-foreground mt-1">
                        Manage software requirement specification documents
                    </p>
                </div>
                <div className="flex gap-2">
                    <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                        <DialogTrigger asChild>
                            <Button>
                                <Plus className="mr-2 h-4 w-4" />
                                Add Specification
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Create New Requirement Specification</DialogTitle>
                                <DialogDescription>
                                    Add a new requirements document (SRS, BRS, FRS, etc.)
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="create-project">
                                        Project <span className="text-red-500">*</span>
                                    </Label>
                                    <Select value={newProjectId} onValueChange={setNewProjectId}>
                                        <SelectTrigger id="create-project">
                                            <SelectValue placeholder="Select project" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {projects?.map((p) => (
                                                <SelectItem key={p.id} value={p.id.toString()}>
                                                    {p.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="create-name">
                                        Name <span className="text-red-500">*</span>
                                    </Label>
                                    <Input
                                        id="create-name"
                                        placeholder="Authentication System Requirements"
                                        value={newName}
                                        onChange={(e) => setNewName(e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="create-docid">
                                        Document ID <span className="text-red-500">*</span>
                                    </Label>
                                    <Input
                                        id="create-docid"
                                        placeholder="SRS-001"
                                        value={newDocId}
                                        onChange={(e) => setNewDocId(e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="create-type">Type</Label>
                                    <Select value={newType} onValueChange={setNewType}>
                                        <SelectTrigger id="create-type">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="SRS">SRS (Software Requirements)</SelectItem>
                                            <SelectItem value="BRS">BRS (Business Requirements)</SelectItem>
                                            <SelectItem value="FRS">FRS (Functional Requirements)</SelectItem>
                                            <SelectItem value="TRS">TRS (Technical Requirements)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="create-title">Title (Optional)</Label>
                                    <Input
                                        id="create-title"
                                        placeholder="Authentication and Authorization Requirements"
                                        value={newTitle}
                                        onChange={(e) => setNewTitle(e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="create-scope">Scope (Optional)</Label>
                                    <Textarea
                                        id="create-scope"
                                        placeholder="Describe the scope of this specification..."
                                        value={newScope}
                                        onChange={(e) => setNewScope(e.target.value)}
                                        rows={3}
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setCreateOpen(false)}>
                                    Cancel
                                </Button>
                                <Button onClick={handleCreate} disabled={createMut.isPending}>
                                    {createMut.isPending && (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    )}
                                    Create Specification
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* Tabs: Browse / Generate */}
            <Tabs defaultValue="browse" className="w-full">
                <TabsList>
                    <TabsTrigger value="browse">
                        <FileText className="h-4 w-4 mr-2" />
                        Browse
                    </TabsTrigger>
                    <TabsTrigger value="generate">
                        <Sparkles className="h-4 w-4 mr-2" />
                        Generate from Code
                    </TabsTrigger>
                </TabsList>

                {/* ================================================================ */}
                {/* Browse Tab                                                       */}
                {/* ================================================================ */}
                <TabsContent value="browse" className="space-y-4">
                    {/* Filters */}
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex flex-wrap gap-4 items-end">
                                <div className="flex-1 min-w-[200px]">
                                    <Label className="mb-1.5 block text-xs text-muted-foreground">
                                        Search
                                    </Label>
                                    <div className="relative">
                                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            placeholder="Search specs..."
                                            className="pl-9"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="w-[180px]">
                                    <Label className="mb-1.5 block text-xs text-muted-foreground">
                                        Project
                                    </Label>
                                    <Select value={projectFilter} onValueChange={setProjectFilter}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="All Projects" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All Projects</SelectItem>
                                            {projects?.map((p) => (
                                                <SelectItem key={p.id} value={p.id.toString()}>
                                                    {p.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="w-[150px]">
                                    <Label className="mb-1.5 block text-xs text-muted-foreground">
                                        Type
                                    </Label>
                                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="All Types" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All Types</SelectItem>
                                            <SelectItem value="SRS">SRS</SelectItem>
                                            <SelectItem value="BRS">BRS</SelectItem>
                                            <SelectItem value="FRS">FRS</SelectItem>
                                            <SelectItem value="TRS">TRS</SelectItem>
                                            <SelectItem value="DRD">DRD (AI Generated)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                {hasFilters && (
                                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                                        <X className="mr-1 h-3 w-3" />
                                        Clear
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Results Table */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">
                                Specifications{' '}
                                {filtered.length > 0 && (
                                    <span className="text-muted-foreground font-normal">
                                        ({filtered.length})
                                    </span>
                                )}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {isLoading ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                                </div>
                            ) : filtered.length > 0 ? (
                                <div className="border rounded-md">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Doc ID</TableHead>
                                                <TableHead>Name</TableHead>
                                                <TableHead>Type</TableHead>
                                                <TableHead>Project</TableHead>
                                                <TableHead className="text-center">Requirements</TableHead>
                                                <TableHead>Created</TableHead>
                                                <TableHead className="text-right">Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {filtered.map((item) => (
                                                <TableRow
                                                    key={`${item.itemType}-${item.id}`}
                                                    className="cursor-pointer"
                                                    onClick={() => navigateToItem(item)}
                                                >
                                                    <TableCell className="font-mono text-sm">
                                                        {item.displayId}
                                                    </TableCell>
                                                    <TableCell className="font-medium">
                                                        {item.displayName}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge
                                                            variant={
                                                                item.displayType === 'DRD' ? 'default' : 'secondary'
                                                            }
                                                        >
                                                            {item.displayType === 'DRD' && (
                                                                <Sparkles className="mr-1 h-3 w-3" />
                                                            )}
                                                            {item.displayType}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground">
                                                        {item.projectName ?? '—'}
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        {item.requirementCount}
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground">
                                                        {formatDate(item.createdAt)}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex items-center justify-end gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    navigateToItem(item)
                                                                }}
                                                            >
                                                                <Eye className="h-4 w-4" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    setDeleteTarget(item)
                                                                }}
                                                            >
                                                                <Trash2 className="h-4 w-4 text-destructive" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="text-center py-12 text-muted-foreground">
                                    <FileText className="mx-auto h-12 w-12 mb-4 opacity-30" />
                                    <p className="font-medium">
                                        {hasFilters
                                            ? 'No specifications match your filters'
                                            : 'No specifications yet'}
                                    </p>
                                    <p className="text-sm mt-1">
                                        {hasFilters
                                            ? 'Try adjusting your filters'
                                            : 'Get started by creating your first requirement specification'}
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ================================================================ */}
                {/* Generate Tab                                                     */}
                {/* ================================================================ */}
                <TabsContent value="generate" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Sparkles className="h-5 w-5" />
                                Generate Requirements from Code
                            </CardTitle>
                            <CardDescription>
                                Upload a codeset archive and the AI engine will generate
                                requirement specifications, test cases, and test plans
                                automatically.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Project selection */}
                            <div className="space-y-2">
                                <Label htmlFor="gen-project">
                                    Project <span className="text-red-500">*</span>
                                </Label>
                                <Select value={genProjectId} onValueChange={setGenProjectId}>
                                    <SelectTrigger id="gen-project">
                                        <SelectValue placeholder="Select project" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {projects?.map((p) => (
                                            <SelectItem key={p.id} value={p.id.toString()}>
                                                {p.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Language */}
                            <div className="space-y-2">
                                <Label htmlFor="gen-lang">
                                    Primary Language <span className="text-red-500">*</span>
                                </Label>
                                <Select value={genLanguage} onValueChange={setGenLanguage}>
                                    <SelectTrigger id="gen-lang">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="python">Python</SelectItem>
                                        <SelectItem value="javascript">JavaScript</SelectItem>
                                        <SelectItem value="typescript">TypeScript</SelectItem>
                                        <SelectItem value="java">Java</SelectItem>
                                        <SelectItem value="csharp">C#</SelectItem>
                                        <SelectItem value="go">Go</SelectItem>
                                        <SelectItem value="rust">Rust</SelectItem>
                                        <SelectItem value="cpp">C++</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Architectural Layer */}
                            <div className="space-y-2">
                                <Label htmlFor="gen-layer" className="flex items-center gap-1.5">
                                    <Layers className="h-3.5 w-3.5" />
                                    Architectural Layer
                                    <span className="text-muted-foreground text-xs font-normal">(optional)</span>
                                </Label>
                                <Select
                                    value={genLayer}
                                    onValueChange={(v) => { setGenLayer(v); setGenLayerCustom('') }}
                                >
                                    <SelectTrigger id="gen-layer">
                                        <SelectValue placeholder="Select layer (optional)" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PRESET_LAYERS.map((l) => (
                                            <SelectItem key={l} value={l}>{l}</SelectItem>
                                        ))}
                                        <SelectItem value="custom">Custom...</SelectItem>
                                    </SelectContent>
                                </Select>
                                {genLayer === 'custom' && (
                                    <Input
                                        placeholder="e.g. data-pipeline"
                                        value={genLayerCustom}
                                        onChange={(e) => setGenLayerCustom(e.target.value)}
                                        className="mt-2"
                                    />
                                )}
                                {resolvedLayer && (
                                    <p className="text-xs text-muted-foreground">
                                        Suite names will be prefixed:{' '}
                                        <span className="font-mono text-foreground">
                                            {resolvedLayer}/&lt;module_name&gt;
                                        </span>
                                    </p>
                                )}
                            </div>

                            {/* File toggle */}
                            <div className="space-y-3">
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="use-existing"
                                        checked={useExistingFile}
                                        onCheckedChange={(c) => {
                                            setUseExistingFile(c as boolean)
                                            if (c) setGenFile(null)
                                            else setGenExistingFileId('')
                                        }}
                                    />
                                    <Label
                                        htmlFor="use-existing"
                                        className="text-sm font-normal cursor-pointer"
                                    >
                                        Use previously uploaded file
                                    </Label>
                                </div>

                                {useExistingFile ? (
                                    <div className="space-y-2">
                                        <Label htmlFor="gen-existing">
                                            Select Existing File <span className="text-red-500">*</span>
                                        </Label>
                                        <Select
                                            value={genExistingFileId}
                                            onValueChange={setGenExistingFileId}
                                        >
                                            <SelectTrigger id="gen-existing">
                                                <SelectValue placeholder="Select a file" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {uniqueAttachments.length > 0 ? (
                                                    uniqueAttachments.map((a) => (
                                                        <SelectItem key={a.id} value={a.id.toString()}>
                                                            {a.file_name} (
                                                            {(a.file_size / 1024 / 1024).toFixed(2)} MB)
                                                        </SelectItem>
                                                    ))
                                                ) : (
                                                    <SelectItem value="none" disabled>
                                                        No files available
                                                    </SelectItem>
                                                )}
                                            </SelectContent>
                                        </Select>
                                        {uniqueAttachments.length === 0 && (
                                            <p className="text-xs text-muted-foreground">
                                                No previously uploaded codeset files found for this project
                                            </p>
                                        )}
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <Label htmlFor="gen-file">
                                            Codeset Archive <span className="text-red-500">*</span>
                                        </Label>
                                        <div className="flex items-center gap-2">
                                            <Input
                                                id="gen-file"
                                                type="file"
                                                accept=".zip,.tar,.tar.gz,.tgz"
                                                onChange={(e) => setGenFile(e.target.files?.[0] ?? null)}
                                            />
                                            {genFile && (
                                                <Badge variant="secondary">
                                                    {(genFile.size / 1024 / 1024).toFixed(2)} MB
                                                </Badge>
                                            )}
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            Accepts .zip, .tar, .tar.gz, or .tgz archives
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Additional generation options */}
                            <div className="space-y-3 border-t pt-4">
                                <Label htmlFor="gen-option" className="text-sm font-medium">
                                    Additional Outputs
                                </Label>
                                <Select
                                    value={genOption}
                                    onValueChange={(v) => setGenOption(v as GenerationOption)}
                                >
                                    <SelectTrigger id="gen-option">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Requirements only</SelectItem>
                                        <SelectItem value="testcases">Also generate test cases</SelectItem>
                                        <SelectItem value="testcases_and_plan">Also generate test cases and test plan</SelectItem>
                                    </SelectContent>
                                </Select>
                                {genOption === 'testcases_and_plan' && (
                                    <p className="text-xs text-muted-foreground">
                                        Test plan generation runs after test cases are created. The full pipeline may take several minutes.
                                    </p>
                                )}
                            </div>

                            {/* Submit */}
                            <Button
                                onClick={handleGenerate}
                                disabled={isSubmitting || !genProjectId}
                                className="w-full"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Uploading &amp; Submitting...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="mr-2 h-4 w-4" />
                                        Generate Requirements
                                    </>
                                )}
                            </Button>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Delete Confirmation */}
            <ConfirmDialog
                open={deleteTarget !== null}
                onOpenChange={(open) => !open && setDeleteTarget(null)}
                title={`Delete ${deleteTarget?.itemType === 'drd' ? 'DRD' : 'Specification'}?`}
                description={`This will permanently delete "${deleteTarget?.displayName}" and all associated requirements. This action cannot be undone.`}
                confirmLabel="Delete"
                variant="destructive"
                loading={deleteMut.isPending || deleteDRDMut.isPending}
                onConfirm={handleConfirmDelete}
            />
        </div>
    )
}

export default ReqSpecsPage